import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { BASE_PATH, BASE_PLUS } from '../domain/routes';

export const isExternalUrl = (destination: string) => /^https?:\/\//.test(destination);

function useNavigationPaths() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const regex = new RegExp(`^${BASE_PATH.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/?`);
  const [parentPath = BASE_PLUS, nestedPath = null, nestedModalPath = null] = pathname
    .replace(regex, '')
    .split('/')
    .filter(Boolean);

  const clearNestedPath = () => navigate(`${BASE_PATH}${parentPath}${window.location.search}`);
  const navigateToBenefit = useCallback(
    (destination: string, replace = false) => {
      if (isExternalUrl(destination)) {
        window.location.assign(destination);
        return;
      }
      navigate(`${BASE_PATH}${parentPath}/${destination}${window.location.search}`, { replace: false });
    },
    [navigate, parentPath],
  );

  const updatePath = (newNestedPath: string, paramToRemove: string | null = null, replace: boolean = true) => {
    const basePath = `${BASE_PATH}${parentPath}${newNestedPath}`;

    if (paramToRemove) {
      searchParams.delete(paramToRemove);
      setSearchParams(searchParams, { replace });
    }
    navigate(`${basePath}${searchParams.toString().length > 0 ? `?${searchParams}` : ''}`, { replace });
  };

  const navigateToNestedModalPath = useCallback(
    (nestedModalPath: string, replace = false) =>
      navigate(`${BASE_PATH}${parentPath}/${nestedPath}/${nestedModalPath}${window.location.search}`, {
        replace: false,
      }),
    [navigate, parentPath, nestedPath],
  );

  const navigateToBaseUrlPath = (baseurl: string = BASE_PATH, replace: boolean = true) => {
    const newPathName = pathname.replace(BASE_PATH, baseurl);
    navigate(`${newPathName}${window.location.search}`, { replace });
  };

  const isVisible = !!nestedPath;
  useEffect(() => {
    document.body.style.position = isVisible ? 'sticky' : 'static';
    document.body.style.overflow = isVisible ? 'hidden' : 'auto';
  }, [isVisible]);

  return {
    parentPath,
    nestedPath,
    nestedModalPath,
    clearNestedPath,
    navigateToBenefit,
    navigateToNestedModalPath,
    navigateToBaseUrlPath,
    queryParams: searchParams,
    updatePath,
  };
}

export default useNavigationPaths;
